import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

const contentTypes = [
  { id: 'gaming', label: 'Gaming' },
  { id: 'fitness', label: 'Fitness & gym' },
  { id: 'irl', label: 'IRL / Just chatting' },
  { id: 'music', label: 'Music' },
  { id: 'creative', label: 'Art & creative' },
  { id: 'sports', label: 'Sports' },
  { id: 'comedy', label: 'Comedy' },
  { id: 'other', label: 'Other' },
];

const goalOptions = [
  { id: 'grow_youtube',   label: 'Grow my YouTube channel' },
  { id: 'grow_following', label: 'Build a bigger following' },
  { id: 'save_time',      label: 'Save time on editing & clipping' },
  { id: 'monetise',       label: 'Monetise my content' },
  { id: 'brand_deals',    label: 'Attract brand deals' },
  { id: 'go_full_time',   label: 'Go full time as a creator' },
];

const frequencyOptions = [
  { id: 'daily',      label: 'Daily' },
  { id: '3-4x',       label: '3–4 times a week' },
  { id: '1-2x',       label: '1–2 times a week' },
  { id: 'occasional', label: 'Occasionally' },
];

const audienceOptions = [
  { id: 'starting', label: 'Just starting out' },
  { id: 'small',    label: 'Under 1,000 followers' },
  { id: 'medium',   label: '1,000 – 10,000' },
  { id: 'large',    label: '10,000 – 100,000' },
  { id: 'xlarge',   label: '100,000+' },
];

function Chip({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding:'8px 16px', borderRadius:20, fontSize:13, fontWeight:500,
        cursor:'pointer', fontFamily:'var(--font-body)', transition:'all 0.15s',
        background: selected ? 'var(--accent)' : 'var(--surface2)',
        color: selected ? '#000' : 'var(--text-dim)',
        border: selected ? '1px solid var(--accent)' : '1px solid var(--border)',
      }}
    >
      {label}
    </button>
  );
}

export default function Onboarding() {
  const [quiz, setQuiz] = useState({ contentTypes:[], goals:[], frequency:'', audienceSize:'' });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/api/auth/questionnaire').then(r => {
      if (r.data?.questionnaire) {
        const q = r.data.questionnaire;
        setQuiz({
          contentTypes: Array.isArray(q.content_type) ? q.content_type : (q.content_type ? [q.content_type] : []),
          goals: q.goals || [],
          frequency: q.posting_frequency || '',
          audienceSize: q.audience_size || '',
        });
      }
      setInitialLoading(false);
    }).catch(() => setInitialLoading(false));
  }, []);

  const toggleContentType = id => setQuiz(q => ({
    ...q, contentTypes: q.contentTypes.includes(id) ? q.contentTypes.filter(c=>c!==id) : [...q.contentTypes, id]
  }));
  const toggleGoal = id => setQuiz(q => ({
    ...q, goals: q.goals.includes(id) ? q.goals.filter(g=>g!==id) : [...q.goals, id]
  }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post('/api/auth/questionnaire', {
        content_type: quiz.contentTypes,
        goals: quiz.goals,
        posting_frequency: quiz.frequency,
        audience_size: quiz.audienceSize,
      });
      navigate('/account');
    } catch {
      navigate('/account');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="auth-page" style={{alignItems:'flex-start', paddingTop:60}}>
      <div className="auth-box" style={{maxWidth:560}}>
        <div className="auth-logo">Clump</div>
        <div style={{fontSize:22, fontFamily:'var(--font-display)', fontWeight:800, letterSpacing:'-0.5px', marginBottom:4}}>
          Update your profile
        </div>
        <div style={{color:'var(--text-muted)', fontSize:13, marginBottom:28}}>
          Update anything that's changed — this helps personalise your insights.
        </div>

        <form onSubmit={handleSubmit} style={{display:'flex', flexDirection:'column', gap:24}}>
          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>What kind of content do you stream? <span style={{color:'var(--text-muted)', fontWeight:400}}>(pick all that apply)</span></div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {contentTypes.map(c => (
                <Chip key={c.id} label={c.label} selected={quiz.contentTypes.includes(c.id)} onClick={() => toggleContentType(c.id)} />
              ))}
            </div>
          </div>

          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>What are your goals? <span style={{color:'var(--text-muted)', fontWeight:400}}>(pick all that apply)</span></div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {goalOptions.map(g => (
                <Chip key={g.id} label={g.label} selected={quiz.goals.includes(g.id)} onClick={() => toggleGoal(g.id)} />
              ))}
            </div>
          </div>

          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>How often do you stream?</div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {frequencyOptions.map(f => (
                <Chip key={f.id} label={f.label} selected={quiz.frequency===f.id} onClick={() => setQuiz(q=>({...q, frequency:f.id}))} />
              ))}
            </div>
          </div>

          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>How big is your current following?</div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {audienceOptions.map(a => (
                <Chip key={a.id} label={a.label} selected={quiz.audienceSize===a.id} onClick={() => setQuiz(q=>({...q, audienceSize:a.id}))} />
              ))}
            </div>
          </div>

          <div style={{display:'flex', gap:12}}>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{flex:1}}>
              {loading ? 'Saving...' : 'Save changes →'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/account')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
